/**
 * One weather variable, over the visible chart, hour by hour.
 *
 * The wind overlay fetches speed AND direction because an arrow needs both. Rain,
 * gusts and pressure need one number per point, and that is all this asks for. It
 * is the same shape as the wind field (a grid of points, a shared hourly time
 * axis, a series per point) with one value instead of four, which is what lets the
 * coloured wash in fieldRaster.ts draw any of them without knowing which it has.
 *
 * ONE VARIABLE PER REQUEST. Open-Meteo rejects the entire request when the chosen
 * model does not publish one of the fields in it, so asking for gusts alongside
 * the wind meant a model with no gust output returned nothing at all and the chart
 * went blank with no error. Asking separately means we lose exactly the thing the
 * model does not have, and can say which model and which variable, which is the
 * difference between a bug report and a sentence on screen.
 *
 * A FINER GRID THAN THE ARROWS. Arrows have to be spaced far enough apart to read
 * individually; a wash has no such limit and its whole value is the gradient, so
 * this grid is denser. Everything between the points is interpolation, not data,
 * which is true of every weather map ever printed and is worth saying once.
 */
import type { RealModelId } from './models';
import { agreeScalar, AGREEMENT_THRESHOLDS } from './consensus';
import { CONSENSUS_MODEL_IDS } from './consensusField';
import type { FieldGrid } from '@/map/fieldRaster';

/** Grid size for the wash. Denser than the 8x6 arrow grid, and still one request. */
export const FIELD_COLS = 12;
export const FIELD_ROWS = 9;

/** Points across [w, s, e, n], row-major from the SOUTH-WEST, matching FieldGrid. */
export function fieldPoints(
  bounds: [number, number, number, number],
  cols = FIELD_COLS,
  rows = FIELD_ROWS,
): [number, number][] {
  const [w, s, e, n] = bounds;
  const out: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out.push([w + ((e - w) * c) / (cols - 1), s + ((n - s) * r) / (rows - 1)]);
    }
  }
  return out;
}

/** The whole fetched field for one variable. */
export interface ScalarField {
  cols: number;
  rows: number;
  bounds: [number, number, number, number];
  /** Hourly timestamps, UTC ms, shared by every point. */
  timesMs: number[];
  /** [pointIndex][hourIndex]. NaN where the model gave nothing. */
  values: number[][];
}

export function buildScalarUrl(
  points: [number, number][],
  modelId: string,
  hourly: string,
  days = 3,
  marine = false,
): string {
  const lat = points.map((p) => p[1].toFixed(4)).join(',');
  const lon = points.map((p) => p[0].toFixed(4)).join(',');
  // Waves live on a different Open-Meteo host with different models. Same JSON
  // shape back, which is why one parser serves both; only the door differs.
  const base = marine
    ? 'https://marine-api.open-meteo.com/v1/marine'
    : 'https://api.open-meteo.com/v1/forecast';
  return (
    base +
    `?latitude=${lat}&longitude=${lon}` +
    `&hourly=${hourly}` +
    `&wind_speed_unit=kn&timezone=UTC&forecast_days=${days}` +
    `&models=${modelId}`
  );
}

interface HourlyLoc {
  hourly?: { time?: string[] } & Record<string, unknown>;
}

export function parseScalarField(
  json: unknown,
  points: [number, number][],
  hourly: string,
  bounds: [number, number, number, number],
  cols = FIELD_COLS,
  rows = FIELD_ROWS,
): ScalarField {
  const arr = (Array.isArray(json) ? json : [json]) as HourlyLoc[];
  let timesMs: number[] = [];
  const values: number[][] = [];
  for (let i = 0; i < points.length; i++) {
    const h = arr[i]?.hourly;
    if (h?.time && !timesMs.length) timesMs = h.time.map((t) => Date.parse(`${t}Z`));
    const raw = h?.[hourly];
    // Number(null) is 0, and 0 mm of rain and "no answer" are different things.
    values.push(Array.isArray(raw) ? raw.map((v) => (v == null ? NaN : Number(v))) : []);
  }
  return { cols, rows, bounds, timesMs, values };
}

/** The hour in the series nearest to `ms`, or -1 if there is no series. */
export function nearestHour(timesMs: number[], ms: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < timesMs.length; i++) {
    const d = Math.abs(timesMs[i] - ms);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Slice the field at one instant into the grid the raster draws. */
export function gridAt(field: ScalarField, ms: number): FieldGrid | null {
  const t = nearestHour(field.timesMs, ms);
  if (t < 0) return null;
  const values = field.values.map((series) => {
    const v = Number(series?.[t]);
    return Number.isFinite(v) ? v : NaN;
  });
  return { cols: field.cols, rows: field.rows, bounds: field.bounds, values };
}

type FetchLike = (
  url: string,
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/**
 * A refusal from Open-Meteo, said in English.
 *
 * A 400 here almost always means one thing: this model does not publish this
 * variable. "Weather fetch failed (400)" sends a skipper to a bug report; naming
 * the model and the variable lets them just pick a different model and carry on.
 */
export function refusalMessage(modelName: string, variableName: string): string {
  return `${modelName} does not publish ${variableName} here. Try another model.`;
}

export async function fetchScalarField(
  bounds: [number, number, number, number],
  // A string, not RealModelId: wave models are their own catalogue and typing
  // this to the atmospheric union would need the cast that caused the
  // models=undefined bug. modelById covers both lists for error wording.
  modelId: string,
  hourly: string,
  days = 3,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
  marine = false,
): Promise<ScalarField> {
  const points = fieldPoints(bounds);
  const r = await fetchImpl(buildScalarUrl(points, modelId, hourly, days, marine));
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return parseScalarField(await r.json(), points, hourly, bounds);
}

/**
 * The same field, agreed across the consensus models.
 *
 * The trimmed mean again, point by point and hour by hour: take the middle, set
 * aside anything sitting far enough away from it, average what is left. It is the
 * same reasoning as the agreed wind field, applied to a single number, and it uses
 * the same per-variable outlier distances so "far enough away" means 5 hPa for
 * pressure and 2 mm for rain rather than one number pretending to fit both.
 *
 * Where fewer than two models answer, the point is left as NaN and draws as a
 * hole. An average of one model is not an average, and dressing it up as one would
 * be the exact dishonesty the blend exists to avoid.
 */
export async function fetchConsensusScalarField(
  bounds: [number, number, number, number],
  hourly: string,
  outlierAt: number,
  days = 3,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<{ field: ScalarField; used: string[] }> {
  const points = fieldPoints(bounds);
  const results = await Promise.all(
    CONSENSUS_MODEL_IDS.map(async (id) => {
      try {
        return { id, field: await fetchScalarField(bounds, id, hourly, days, fetchImpl) };
      } catch {
        return { id, field: null };
      }
    }),
  );
  const ok = results.filter((r): r is { id: RealModelId; field: ScalarField } => r.field !== null);
  if (!ok.length) throw new Error('No model answered');

  // The longest time axis wins, so a short model does not truncate the blend.
  const base = ok.reduce((a, b) => (b.field.timesMs.length > a.field.timesMs.length ? b : a)).field;
  const values: number[][] = [];
  for (let p = 0; p < points.length; p++) {
    const series: number[] = [];
    for (let t = 0; t < base.timesMs.length; t++) {
      const ms = base.timesMs[t];
      const cell = ok.map((m) => {
        const i = nearestHour(m.field.timesMs, ms);
        // Only accept a model's value if it really has that hour, not the nearest
        // one three hours away, which is what a short model would otherwise offer.
        const close = i >= 0 && Math.abs(m.field.timesMs[i] - ms) <= 1_800_000;
        const v = close ? Number(m.field.values[p]?.[i]) : NaN;
        return { modelId: m.id, value: Number.isFinite(v) ? v : null };
      });
      series.push(trimmedMeanOf(cell, outlierAt));
    }
    values.push(series);
  }
  return {
    field: { cols: FIELD_COLS, rows: FIELD_ROWS, bounds, timesMs: base.timesMs, values },
    used: ok.map((m) => m.id),
  };
}

/** Mean of the values not flagged as outliers. NaN if fewer than two answered. */
export function trimmedMeanOf(
  values: { modelId: string; value: number | null }[],
  outlierAt: number,
): number {
  const c = agreeScalar(values, {
    tightBelow: AGREEMENT_THRESHOLDS.windKn.tightBelow,
    poorAbove: AGREEMENT_THRESHOLDS.windKn.poorAbove,
    outlierAt,
  });
  if (c.value == null || c.count < 2) return NaN;
  const kept = values
    .filter((v) => v.value != null && Number.isFinite(v.value))
    .filter((v) => !c.outliers.includes(v.modelId))
    .map((v) => v.value as number);
  // Everything flagged means the models are all over the place and there is no
  // sensible middle to trim towards, so fall back to the median rather than to
  // nothing. The blend still draws; it is just the median that draws.
  if (!kept.length) return c.value;
  return kept.reduce((a, b) => a + b, 0) / kept.length;
}
